import type { Node } from '../../models/scene';
import {
  computeGenogramPersonLabelBounds,
  type PersonLabelGeometryContext,
} from '../../../lib/diagram/personLabelBounds';
import { resolveFiniteNodeRect } from './nodeRect';

// Shared helper to normalize connector anchor rects across all creation paths.

const resolveNode = <T extends Node>(
  node: T,
  getShapeState?: <K extends Record<string, any>>(shapeId: string, baseState: K) => K,
): T => {
  return getShapeState ? (getShapeState(node.id, node) as T) : node;
};

export const getAnchorRect = (
  node: Node,
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T,
) => {
  const n = resolveNode(node, getShapeState);
  return resolveFiniteNodeRect(n).rect;
};

export const getBottomCenter = (
  node: Node,
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T,
) => {
  const rect = getAnchorRect(node, getShapeState);
  return { x: rect.x + rect.w / 2, y: rect.y + rect.h };
};

export const getTopCenter = (
  node: Node,
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T,
) => {
  const rect = getAnchorRect(node, getShapeState);
  return { x: rect.x + rect.w / 2, y: rect.y };
};

export const getMarriageTerminalBottomCenter = (
  node: Node,
  getShapeState?: Parameters<typeof getBottomCenter>[1],
  labelGeometry?: PersonLabelGeometryContext,
): { x: number; y: number } => {
  const terminal = getBottomCenter(node, getShapeState);
  const resolved = resolveNode(node, getShapeState);
  if (resolved.type !== 'person') return terminal;

  const rect = resolveFiniteNodeRect(resolved).rect;
  const record = resolved as unknown as Record<string, unknown>;
  const props =
    resolved.props && typeof resolved.props === 'object'
      ? resolved.props
      : {};
  const propsAttrs =
    props.attrs && typeof props.attrs === 'object'
      ? props.attrs as Record<string, unknown>
      : null;
  const attrs = propsAttrs ??
    (resolved.attrs && typeof resolved.attrs === 'object' ? resolved.attrs : {});
  const labelName = [resolved.name, props.name, attrs.name].find(
    (value): value is string => typeof value === 'string' && value.trim().length > 0,
  ) ?? 'Person';
  const rotationValue = attrs.rotation ?? record.rotation ?? props.rotation;
  const rotation =
    typeof rotationValue === 'number' && Number.isFinite(rotationValue)
      ? rotationValue
      : undefined;
  const labelBounds = computeGenogramPersonLabelBounds({
    type: 'person',
    position: { x: rect.x, y: rect.y },
    size: { width: rect.w, height: rect.h },
    rotation,
    props: {
      ...record,
      ...props,
      name: labelName,
      __exportLine2Field:
        labelGeometry
          ? labelGeometry.line2Field
          : props.__exportLine2Field ?? attrs.__exportLine2Field ?? null,
      attrs,
    },
  });

  return {
    x: terminal.x,
    y: Math.max(terminal.y, labelBounds?.maxY ?? terminal.y),
  };
};

export const getMarriageBaselineYShared = (
  fromNode: Node,
  toNode: Node,
  getShapeState?: <T extends Record<string, any>>(shapeId: string, baseState: T) => T,
  labelGeometry?: PersonLabelGeometryContext,
): number => {
  const p0 = getMarriageTerminalBottomCenter(fromNode, getShapeState, labelGeometry);
  const p1 = getMarriageTerminalBottomCenter(toNode, getShapeState, labelGeometry);
  return Math.round(Math.max(p0.y, p1.y) + 12);
};
